import React from 'react';
import { cx } from './cx';

/**
 * Segmented tabs. `tabs`: [{ key, label, count? }]. Controlled via `value` / `onChange`.
 * Arrow keys move between tabs (roving tabindex), per the WAI-ARIA tabs pattern.
 */
export interface TabDef {
  key: string;
  label: string;
  count?: number;
}

interface TabsProps {
  tabs: TabDef[];
  value: string;
  onChange: (key: string) => void;
  label?: string;
}

export function Tabs({ tabs, value, onChange, label = 'Sections' }: TabsProps) {
  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    onChange(tabs[next].key);
    document.getElementById(`tab-${tabs[next].key}`)?.focus();
  };
  return (
    <div role="tablist" aria-label={label} className="inline-flex gap-1 rounded-xl bg-chip p-1">
      {tabs.map((t, i) => {
        const selected = t.key === value;
        return (
          <button
            key={t.key}
            id={`tab-${t.key}`}
            role="tab"
            type="button"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(t.key)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cx('rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors', selected ? 'bg-surface text-ink shadow-card' : 'text-muted hover:text-ink')}
          >
            {t.label}
            {typeof t.count === 'number' && <span className="ml-1.5 text-muted">· {t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
